import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

export type NotificationSettings = {
  email_enabled: boolean;
  email_match_alert: boolean;
  email_upcoming_session_reminder: boolean;
  email_session_cancellation: boolean;
  email_rescheduling_request: boolean;
  email_platform_announcement: boolean;
  email_new_message: boolean;
  in_app_enabled: boolean;
  in_app_upcoming_session_reminder: boolean;
  in_app_session_changes: boolean;
  in_app_new_student_assigned: boolean;
  in_app_new_message: boolean;
  in_app_action_required: boolean;
  in_app_attendance_update: boolean;
  in_app_platform_update: boolean;
};

type NotificationSettingsState = {
  loading: boolean;
  saving: boolean;
  error: boolean;
  errorMessage: string;
  data: NotificationSettings | null;
};

const initialState: NotificationSettingsState = {
  loading: false,
  saving: false,
  error: false,
  errorMessage: '',
  data: null,
};

const coerceNotificationSettings = (data: any): NotificationSettings => {
  const payload = data?.response ?? data ?? {};
  return {
    email_enabled: Boolean(payload.email_enabled),
    email_match_alert: Boolean(payload.email_match_alert),
    email_upcoming_session_reminder: Boolean(
      payload.email_upcoming_session_reminder
    ),
    email_session_cancellation: Boolean(payload.email_session_cancellation),
    email_rescheduling_request: Boolean(payload.email_rescheduling_request),
    email_platform_announcement: Boolean(payload.email_platform_announcement),
    email_new_message: Boolean(payload.email_new_message),
    in_app_enabled: Boolean(payload.in_app_enabled),
    in_app_upcoming_session_reminder: Boolean(
      payload.in_app_upcoming_session_reminder
    ),
    in_app_session_changes: Boolean(payload.in_app_session_changes),
    in_app_new_student_assigned: Boolean(payload.in_app_new_student_assigned),
    in_app_new_message: Boolean(payload.in_app_new_message),
    in_app_action_required: Boolean(payload.in_app_action_required),
    in_app_attendance_update: Boolean(payload.in_app_attendance_update),
    in_app_platform_update: Boolean(payload.in_app_platform_update),
  };
};

export const getNotificationSettings = createAsyncThunk<NotificationSettings>(
  'getNotificationSettings',
  async () => {
    const { data } = await api.get('/api/notifications/settings');
    return coerceNotificationSettings(data);
  }
);

export const updateNotificationSettings = createAsyncThunk<
  NotificationSettings,
  NotificationSettings
>('updateNotificationSettings', async (payload) => {
  const { data } = await api.put('/api/notifications/settings', payload);
  if (!data) {
    throw new Error('Missing update response.');
  }
  return payload;
});

const notificationSettingsSlice = createSlice({
  name: 'notificationSettings',
  initialState,
  reducers: {
    resetNotificationSettings: (state) => {
      state.loading = false;
      state.saving = false;
      state.error = false;
      state.errorMessage = '';
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotificationSettings.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(getNotificationSettings.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(getNotificationSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to load notification settings.';
    });
    builder.addCase(updateNotificationSettings.pending, (state) => {
      state.saving = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(updateNotificationSettings.fulfilled, (state, action) => {
      state.saving = false;
      state.data = action.payload;
    });
    builder.addCase(updateNotificationSettings.rejected, (state, action) => {
      state.saving = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to update notification settings.';
    });
  },
});

export const { resetNotificationSettings } = notificationSettingsSlice.actions;
export default notificationSettingsSlice.reducer;
